import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Bug, Lightbulb, MessageCircle, HelpCircle, Send, ChevronDown, MessageSquareText } from 'lucide-react';
import { Shell } from '../components/layout/Shell';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { useCreateFeedback, useFeedbackList } from '../hooks/useFeedback';
import { useTranslation } from '../i18n';
import type { TranslationKey } from '../i18n/en';
import type { FeedbackType, FeedbackStatus } from '@siliconvault/shared';
import { Route as rootRoute } from './__root';
import styles from './feedback.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/feedback',
  component: FeedbackPage,
});

const TYPE_CONFIG: { type: FeedbackType; icon: typeof Bug; labelKey: TranslationKey }[] = [
  { type: 'bug', icon: Bug, labelKey: 'feedback.typeBug' },
  { type: 'feature', icon: Lightbulb, labelKey: 'feedback.typeFeature' },
  { type: 'feedback', icon: MessageCircle, labelKey: 'feedback.typeFeedback' },
  { type: 'question', icon: HelpCircle, labelKey: 'feedback.typeQuestion' },
];

function getTypeIcon(type: string) {
  switch (type) {
    case 'bug': return Bug;
    case 'feature': return Lightbulb;
    case 'feedback': return MessageCircle;
    case 'question': return HelpCircle;
    default: return MessageCircle;
  }
}

function statusClass(status: string) {
  switch (status) {
    case 'open': return styles.statusOpen;
    case 'in_progress': return styles.statusInProgress;
    case 'resolved': return styles.statusResolved;
    case 'closed': return styles.statusClosed;
    default: return styles.statusOpen;
  }
}

const STATUS_LABEL_KEYS: Record<FeedbackStatus, TranslationKey> = {
  open: 'feedback.statusOpen',
  in_progress: 'feedback.statusInProgress',
  resolved: 'feedback.statusResolved',
  closed: 'feedback.statusClosed',
};

function FeedbackPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const createFeedback = useCreateFeedback();
  const { data: ticketsData, isLoading } = useFeedbackList();

  const [type, setType] = useState<FeedbackType>('feedback');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const tickets = ticketsData?.data ?? [];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await createFeedback.mutateAsync({ type, title: title.trim(), description: description.trim() });
      setTitle('');
      setDescription('');
      toast(t('feedback.submitted'), 'success');
    } catch {
      toast('Failed to submit feedback', 'error');
    }
  }

  return (
    <Shell>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('feedback.title')}</h1>
          <p className={styles.subtitle}>{t('feedback.subtitle')}</p>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          {/* Type selector */}
          <div className={styles.typeSelector}>
            {TYPE_CONFIG.map(({ type: t2, icon: Icon, labelKey }) => (
              <button
                key={t2}
                type="button"
                className={type === t2 ? styles.typeBtnActive : styles.typeBtn}
                onClick={() => setType(t2)}
              >
                <Icon size={14} strokeWidth={1.75} />
                {t(labelKey)}
              </button>
            ))}
          </div>

          {/* Title */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('feedback.titleLabel')}</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('feedback.titlePlaceholder')}
              required
            />
          </div>

          {/* Description */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>{t('feedback.descriptionLabel')}</label>
            <textarea
              className={styles.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('feedback.descriptionPlaceholder')}
              rows={4}
            />
          </div>

          <div className={styles.submitRow}>
            <Button type="submit" disabled={!title.trim() || createFeedback.isPending}>
              <Send size={14} strokeWidth={1.75} />
              {t('feedback.submit')}
            </Button>
          </div>
        </form>

        {/* My Tickets */}
        <div className={styles.ticketsSection}>
          <h2 className={styles.ticketsTitle}>{t('feedback.myTickets')}</h2>

          {isLoading ? (
            <div className={styles.loading}>Loading...</div>
          ) : tickets.length === 0 ? (
            <div className={styles.empty}>
              <MessageSquareText size={40} strokeWidth={1} className={styles.emptyIcon} />
              <div className={styles.emptyTitle}>{t('feedback.noTickets')}</div>
              <div className={styles.emptyDesc}>{t('feedback.noTicketsDesc')}</div>
            </div>
          ) : (
            <div className={styles.tickets}>
              {tickets.map((ticket: any) => {
                const Icon = getTypeIcon(ticket.type);
                const expanded = expandedId === ticket.id;
                return (
                  <div key={ticket.id} className={styles.ticket}>
                    <div
                      className={styles.ticketHeader}
                      onClick={() => setExpandedId(expanded ? null : ticket.id)}
                    >
                      <span className={styles.ticketIcon}>
                        <Icon size={16} strokeWidth={1.75} />
                      </span>
                      <span className={styles.ticketTitle}>{ticket.title}</span>
                      <div className={styles.ticketMeta}>
                        <span className={statusClass(ticket.status)}>
                          {t(STATUS_LABEL_KEYS[ticket.status as FeedbackStatus] ?? 'feedback.statusOpen')}
                        </span>
                        <span className={styles.ticketDate}>
                          {new Date(ticket.createdAt).toLocaleDateString()}
                        </span>
                        <ChevronDown
                          size={14}
                          strokeWidth={1.75}
                          className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`}
                        />
                      </div>
                    </div>
                    {expanded && (
                      <div className={styles.ticketBody}>
                        {ticket.description && (
                          <div className={styles.ticketDescription}>{ticket.description}</div>
                        )}
                        {ticket.adminReply ? (
                          <div className={styles.adminReply}>
                            <div className={styles.adminReplyLabel}>{t('feedback.adminReply')}</div>
                            <div className={styles.adminReplyText}>{ticket.adminReply}</div>
                          </div>
                        ) : (
                          <div className={styles.noReply}>{t('feedback.noReply')}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
